import { useCallback, useEffect, useRef, useState } from 'react';
import type { BanepadDeviceRegistration } from '../../engine/banepadDevice';
import { padScreenProps } from '../../lib/pad/padScreenProps';
import { fetchPadState, submitPadLiveScore, type PadResultScore } from '../../lib/api/pad';
import { usePadResultSubmission } from '../../state/usePadResultSubmission';
import { BanepadDeviceDisplay } from './BanepadDeviceDisplay';
import { BanepadDeviceOffline } from './BanepadDeviceOffline';
import { padLastKnownFrom, readPadLastKnown, writePadLastKnown } from '../../lib/banepadLastKnown';
import { BanepadOfflineBanner } from './BanepadOfflineBanner';
import { useBanepadHeartbeat } from '../../state/useBanepadHeartbeat';

export interface BanepadDeviceShellProps {
  /** The paired registration resolved from this tablet's device token. */
  registration: BanepadDeviceRegistration;
}

/** How often the pad re-reads its own court. */
export const PAD_STATE_POLL_MS = 10_000;

/**
 * How long the pad KEEPS SHOWING THE MATCH after the server has applied its
 * result (#216).
 *
 * From the hall: «it was faster to jump out of the match. A bit too fast. Did
 * my result go through, it just say reserved.» A result applies in a couple of
 * hundred milliseconds and consumes the match, so the court's honest next
 * state is a plain booking — which arrives before the person who pressed the
 * button has looked up from it.
 *
 * So the applied view is held for a moment while the button says «Registrert».
 * The pad is NOT pretending: the result is in, the server said so, and this
 * frame is the acknowledgement. Polls are ignored for the same moment, or the
 * ten-second re-read would yank the screen out from under it anyway.
 */
export const PAD_RESULT_HOLD_MS = 2_200;

/**
 * COLD START: how long the pad waits for its FIRST `/pad/state` answer before
 * it stops showing nothing and says it cannot reach Starpoint (#479).
 *
 * A wall-mounted tablet reboots for ordinary reasons — a power blip, an Android
 * update, somebody unplugging it — and doing so during an outage used to leave
 * it on the dark splash below FOR EVER, with no explanation and nothing to
 * press. The splash itself is right: flashing «ledig» at a court in play is
 * worse than showing nothing. It just has to end.
 *
 * WHY TWENTY SECONDS, and not the 2 min the live connection gets
 * (`BANEPAD_PAD_OFFLINE_AFTER_MS`): the two situations are not the same one.
 *
 *   a running pad   has a court on screen, and that court is worth protecting
 *                   from one missed beat. Two minutes buys the blip out.
 *   a cold start    has NOTHING on screen. There is no state to protect and
 *                   nothing to interrupt, so the only cost of speaking early is
 *                   telling a pad whose first read is merely slow that the
 *                   network is down — and it takes that back by itself on the
 *                   next successful poll.
 *
 * Twenty seconds is two full `PAD_STATE_POLL_MS` cycles, so the pad has tried
 * and failed at least twice before it says so; a slow-but-working hall network
 * is not reported, and a genuinely unreachable one stops being a blank tablet
 * within twenty seconds instead of never.
 */
export const PAD_COLD_START_OFFLINE_AFTER_MS = 2 * PAD_STATE_POLL_MS;

// ─────────────────────────────────────────────────────────────────────
// THE PHYSICAL PAD, ON THE SERVER'S DATA (#185).
//
// It used to mount a StoreProvider — the club's entire live dataset, every
// court, every player — and filter to its own court in the browser. A tablet in
// a corridor held the lot, and "a pad cannot read another court" was not a
// property of anything, because there was nothing to stop it.
//
// Now it reads `/pad/state` with its DEVICE token and renders what comes back.
// The court is the server's answer, not a filter this component applies.
//
// WHAT IS COMMITTED COMES FROM THE SERVER, ALWAYS. Recording a result POSTs the
// score the operator entered; the response is the new court view, and that is
// what re-renders. The pad computes no result, no rating and no table — so it
// cannot disagree with the club, and there is no local copy to drift.
//
// The one optimistic thing is the operator's own input while a submission is in
// flight: theirs, and on screen, because blanking it mid-outage would be worse
// than useless. See usePadResultSubmission.
// ─────────────────────────────────────────────────────────────────────
export function BanepadDeviceShell({ registration }: BanepadDeviceShellProps) {
  // NO TOKEN TO HOLD (#663). The credential is an httpOnly cookie the transport
  // carries on its own, and this component is only reached once the route has
  // resolved a registration — so «are we paired» is already answered above.
  const [view, setView] = useState<Record<string, unknown> | null>(null);
  const [poll, setPoll] = useState(0);
  // Keepalive to Sentralen + the pad's own «no connection» screen.
  const heartbeat = useBanepadHeartbeat(registration);

  // Live clock, ticked here and threaded all the way down (#324) so every
  // renderer judges "is this match now?" against the same instant.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  /**
   * True while an applied result is being acknowledged on screen. A poll
   * landing in this window must not replace the frame the operator is reading.
   */
  const holdingRef = useRef(false);

  /**
   * COLD START (#479): when this pad started asking, and whether it has waited
   * long enough to admit it got no answer. Consulted only while there is no
   * view — once the court has been read once, the outage handling below (#373)
   * takes over and keeps the match on screen.
   */
  const askingSince = useRef(Date.now());
  const [coldStartTimedOut, setColdStartTimedOut] = useState(false);
  useEffect(() => {
    if (view) return undefined;
    const id = setTimeout(() => setColdStartTimedOut(true), PAD_COLD_START_OFFLINE_AFTER_MS);
    return () => clearTimeout(id);
  }, [view]);

  // Re-read the court. A failed poll KEEPS the last view: a pad that has been
  // rendering for an hour must not blank itself because one request missed.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const next = await fetchPadState();
        if (!cancelled && next && !holdingRef.current) {
          setView(next);
          // WHAT THE COURT WAS DOING, kept for the next cold start (#479).
          //
          // Written from the SERVER's answer, on the way past, and only ever
          // read by the offline screen below. It is a note for a person, not a
          // view: see lib/banepadLastKnown.ts for why it cannot be rendered as
          // a court, and why that is deliberate.
          //
          // A null note is not stored: an idle court has nothing to report, and
          // overwriting a real match with «idle» the moment a tournament ends
          // would throw away the one thing worth keeping.
          const note = padLastKnownFrom(next);
          if (note) writePadLastKnown(note);
        }
      } catch {
        /* keep the last view; the interval below tries again */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [poll]);

  useEffect(() => {
    const id = setTimeout(() => setPoll((n) => n + 1), PAD_STATE_POLL_MS);
    return () => clearTimeout(id);
  }, [poll]);

  /**
   * A result the SERVER applied comes back as the new court view — HELD for a
   * moment before it replaces the match. See PAD_RESULT_HOLD_MS.
   */
  const onApplied = useCallback((next: Record<string, unknown>) => {
    if (!next || typeof next !== 'object' || !('court' in next)) return;
    holdingRef.current = true;
    setTimeout(() => {
      holdingRef.current = false;
      setView(next);
    }, PAD_RESULT_HOLD_MS);
  }, []);
  // PAIRED RATHER THAN A TOKEN (#663). This component only renders once the route
  // has a registration, so it is paired by construction — passed explicitly all the
  // same, because the hook's gate is «may we submit at all» and it should keep
  // saying so rather than inferring it.
  const submission = usePadResultSubmission(true, onApplied);

  // A refusal means the pad is out of step with the court — re-read rather than
  // argue with the server about it.
  const refused = submission.state.status === 'refused';
  const acknowledge = submission.acknowledge;
  const refusalText = submission.state.status === 'refused' ? submission.state.error : '';

  /**
   * WHAT THE COURT IS TOLD ABOUT ITS OWN WRITE (#216).
   *
   * Reported from a hall: «send result, nothing happens. Send result, nothing
   * happens… send result… court is reserved.» The submission was in flight and
   * then applied, and the pad said nothing at any point — so the operator
   * pressed four times and learned it had worked only because the screen
   * changed underneath them.
   *
   * The server refused the extra presses (the plan is consumed on the first
   * one), so nothing was rated twice. That is a guard holding, not a reason to
   * leave a kiosk silent: on a slow hall network the same silence is a person
   * pressing until something happens.
   *
   * A refusal was worse than silent — it re-read the court and cleared itself,
   * so «already finalised» looked identical to «nothing happened».
   */
  const [refusalNotice, setRefusalNotice] = useState('');
  useEffect(() => {
    if (!refused) return undefined;
    setRefusalNotice(refusalText || 'Resultatet ble avvist');
    setPoll((n) => n + 1);
    acknowledge();
    const id = setTimeout(() => setRefusalNotice(''), 6000);
    return () => clearTimeout(id);
  }, [refused, refusalText, acknowledge]);

  const pending = submission.state.status === 'pending' ? submission.state.pending : null;
  const confirmed = submission.state.status === 'confirmed' ? submission.state.confirmed : null;

  /**
   * WHAT THE BUTTON ITSELF SAYS (#216).
   *
   * «My eyes focused on the submit button after clicking it. It is human
   * nature.» — so the phase goes back down to the control that started it,
   * matched by the id the submission named, and the banner below is the second
   * line of defence rather than the only one.
   */
  const submitStatus = {
    phase: (pending ? 'sending' : confirmed ? 'done' : 'idle') as 'idle' | 'sending' | 'done',
    matchId: pending?.matchId ?? confirmed?.matchId ?? null,
  };

  /**
   * Is there anything on this court for the operator to act on? (#373)
   *
   * Read from the LAST view rather than a fresh one, which is the whole
   * point: the poll above keeps the previous court view when a read fails, so
   * during an outage the pad still knows what is on it. That is «the match
   * state is already present», and it is what separates «report the outage»
   * from «take the screen».
   */
  const hasMatchOnCourt = ((view ?? {}) as { match?: unknown }).match != null;

  const props = padScreenProps({
    court: registration.courtId,
    mode: String((view ?? {}).mode ?? 'idle'),
    screen: ((view ?? {}).screen ?? null) as never,
  });

  /**
   * The id a submission names for this court.
   *
   * The enkeltmatch pad hands back an ACTIVITY, and the command compares the
   * linked BOOKING's id — so the id comes from the projection, not from the
   * thing on screen. Every other renderer supplies its own match id.
   */
  const submissionId = props.submissionMatchId;
  const sendLive = useCallback((matchId: string | null, liveScore: unknown) => {
    if (!matchId) return;
    void submitPadLiveScore(matchId, liveScore);
  }, []);

  const send = submission.submit;
  const callbacks = {
    // padel24 + the americano family: two numbers.
    onRecordResult: (matchId: string, s1: number, s2: number) =>
      send(matchId, { kind: 'points', score1: s1, score2: s2 } as PadResultScore),
    // KM and seriespill hand back a match they finished LOCALLY. Only its id
    // and the entered score are used; everything the renderer computed about
    // the result is discarded, because the server computes it.
    onRecordKMResult: (updatedMatch: Record<string, unknown>) =>
      send(String(updatedMatch?.id ?? ''), {
        kind: 'sets',
        score: (updatedMatch?.score ?? {}) as Record<string, unknown>,
      }),
    onRecordSerieResult: (updatedMatch: Record<string, unknown>) =>
      send(String(updatedMatch?.id ?? ''), {
        kind: 'sets',
        score: (updatedMatch?.score ?? {}) as Record<string, unknown>,
      }),
    // enkeltmatch: the booking's id, and the sets entered.
    onRecordSingleMatchResult: (_activity: unknown, data: Record<string, unknown>) =>
      submissionId &&
      send(submissionId, { kind: 'singleMatch', sets: (data?.sets ?? []) as unknown[] }),

    onRecordPadLiveScore: (matchId: string, liveScore: unknown) => sendLive(matchId, liveScore),
    onRecordKMLiveScore: (m: Record<string, unknown>) =>
      sendLive(String(m?.id ?? ''), m?.liveScore ?? null),
    onRecordSerieLiveScore: (m: Record<string, unknown>) =>
      sendLive(String(m?.id ?? ''), m?.liveScore ?? null),
    onLiveSingleMatchScore: (_activity: unknown, liveScore: unknown) =>
      sendLive(submissionId, liveScore),
  };

  // Dark splash until the court has been read once, so a reload never flashes
  // an idle screen at a court that is in play.
  //
  // ── BUT BOUNDED, OR IT IS INDISTINGUISHABLE FROM A DEAD TABLET (#479) ──
  //
  // After the bound, the splash becomes the offline screen: the court, how long
  // it has been trying, who to contact, and a retry. NOTHING IS INVENTED — the
  // court name comes from the registration the server already resolved, and the
  // pad still renders no court, no match and no idle screen it has not been
  // told about. «Still loading» and «cannot reach Starpoint» are the only two
  // honest things to say before `/pad/state` answers, and this picks between
  // them on time rather than never.
  //
  // `heartbeat.offline` short-circuits the wait because it carries the
  // browser's own `navigator.onLine === false`, which is PROOF nothing can
  // succeed rather than evidence it is slow — the same asymmetry the running
  // pad already uses (see useBanepadHeartbeat).
  //
  // Recovery needs nobody: the poll above keeps running, so the first read that
  // succeeds replaces this with the court. The button only shortcuts the timer.
  if (!view) {
    const unreachable = heartbeat.offline || coldStartTimedOut;
    // Read at RENDER time, not at mount: on this path the pad has no view, so
    // there is no state for it to conflict with, and reading it here means a
    // note written moments before a reboot is the one that shows.
    const lastKnown = unreachable ? readPadLastKnown(String(registration.courtId)) : null;
    return (
      <div className="h-dvh w-full" style={{ background: '#0a0b10' }}>
        {unreachable && (
          <BanepadDeviceOffline
            courtName={registration.courtName}
            since={askingSince.current}
            lastKnown={lastKnown}
            onRetry={() => {
              // Re-read the COURT, not just the heartbeat: the missing
              // `/pad/state` answer is the whole problem on this screen.
              setPoll((n) => n + 1);
              heartbeat.retry();
            }}
          />
        )}
      </div>
    );
  }

  return (
    <>
      <BanepadDeviceDisplay
        courtId={registration.courtId}
        courtName={registration.courtName}
        courtMeta={props.courtMeta}
        now={now}
        bookings={props.bookings}
        tournament={props.tournament}
        activeSeries={props.activeSeries}
        forceTournament={props.forceTournament}
        forceSeries={props.forceSeries}
        forceSingleMatch={props.forceSingleMatch}
        activities={[]}
        plannedMatches={[]}
        allPlayers={props.allPlayers}
        projected={props.projected}
        callbacks={callbacks}
        submitStatus={submitStatus}
      />
      {(pending || confirmed || refusalNotice) && (
        <div
          role="status"
          aria-live="polite"
          className="fixed left-1/2 -translate-x-1/2 rounded-full px-5 py-2 font-semibold"
          style={{
            bottom: '4vh',
            zIndex: 60,
            fontSize: 'min(3.2vw, 20px)',
            background: refusalNotice ? '#7f1d1d' : confirmed ? '#14532d' : '#0f172a',
            color: '#e2e8f0',
            border: `1px solid ${refusalNotice ? '#ef4444' : confirmed ? '#22c55e' : '#334155'}`,
          }}
        >
          {refusalNotice
            ? refusalNotice
            : pending
              ? pending.attempts > 1
                ? `Sender resultat… (forsøk ${pending.attempts})`
                : 'Sender resultat…'
              : 'Resultatet er registrert'}
        </div>
      )}

      {/* ── LOSING THE NETWORK MUST NOT COST A COURT ITS MATCH (#373) ──
          Reported from the hall: «I cannot enter anything while offline. It
          just show a offline screen.» That was accurate, and it was this
          overlay: `fixed inset-0`, covering the very buttons the operator
          needed. The retry queue behind them was working — it carried a
          result through an outage, eleven attempts, applied exactly once —
          and nothing could reach it.

          So the overlay is now for the case it was right for. A court with a
          match ON it gets the state reported and keeps its controls; a court
          with nothing on it gets the full screen, because there is nothing to
          take away and a wall-mounted tablet showing a support number beats a
          dead idle screen.

          `match` COVERS NEXT-UP AS WELL AS LIVE, deliberately. A pad that is
          about to start a match is not a pad with nothing to do, and offline
          is exactly when it cannot fetch what it is missing. */}
      {heartbeat.offline &&
        heartbeat.offlineSince != null &&
        (hasMatchOnCourt ? (
          <BanepadOfflineBanner
            since={heartbeat.offlineSince}
            // The shell's clock is a Date; the banner only needs the instant.
            now={now.getTime()}
            sending={pending != null}
          />
        ) : (
          <BanepadDeviceOffline
            courtName={registration.courtName}
            since={heartbeat.offlineSince}
            onRetry={heartbeat.retry}
          />
        ))}
    </>
  );
}
